import { store } from "@/app/store";
import { mediaApi, surveyApi, userApi } from "@/services/base";
import { useVisibilityChange } from "@uidotdev/usehooks";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useRef } from "react";

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);

const LAST_VISIT_KEY = "app_last_visit_timestamp";
const REFETCH_ON_FOCUS_KEY = "app_refetch_on_focus";

// 5 minutes in milliseconds
const REFETCH_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Function to refetch all relevant APIs
 */
const refetchAllApis = () => {
  try {
    // Reset API state to clear cache and trigger refetch
    store.dispatch(surveyApi.util.resetApiState());
    store.dispatch(userApi.util.resetApiState());
    store.dispatch(mediaApi.util.resetApiState());
  } catch (_error) {
    console.error("[Visit Tracking] Error refetching APIs");
  }
};

/**
 * Hook to track window visit timing and automatically refetch APIs when needed
 * Uses @uidotdev/usehooks useVisibilityChange for visibility detection
 *
 * Behavior:
 * 1. When window becomes visible (user returns after being away)
 * 2. Checks time elapsed since last visit
 * 3. If > 5 minutes: automatically refetches all RTK Query APIs
 * 4. Stores timestamp in localStorage for persistence across page reloads
 *
 * Returns:
 * - getTimeSinceLastVisit: Function to get time since last visit in milliseconds
 * - getLastVisitTimestamp: Function to get last visit timestamp
 * - formatTimeDifference: Function to format milliseconds to readable string
 */
export const useWindowVisitTracking = () => {
  const hasRefetched = useRef(false);
  const documentVisible = useVisibilityChange();

  // Initialize on mount
  useEffect(() => {
    const now = Date.now();
    const lastVisitStr = localStorage.getItem(LAST_VISIT_KEY);
    const lastVisit = lastVisitStr ? parseInt(lastVisitStr, 10) : null;

    // Calculate time difference on mount
    if (lastVisit !== null) {
      const timeDifference = now - lastVisit;
      const shouldRefetch = timeDifference > REFETCH_THRESHOLD_MS;
      localStorage.setItem(REFETCH_ON_FOCUS_KEY, shouldRefetch.toString());

      // If component mounted after 5+ minutes and hasn't refetched yet, do it now
      if (shouldRefetch && !hasRefetched.current) {
        hasRefetched.current = true;
        refetchAllApis();
      }
    } else {
      // First visit - no refetch needed
      localStorage.setItem(REFETCH_ON_FOCUS_KEY, "false");
    }

    // Update last visit timestamp
    localStorage.setItem(LAST_VISIT_KEY, now.toString());
  }, []);

  // Handle visibility changes using @uidotdev/usehooks
  useEffect(() => {
    if (documentVisible === true) {
      // Window became visible again - calculate time difference
      const currentTime = Date.now();
      const storedLastVisit = localStorage.getItem(LAST_VISIT_KEY);
      const lastVisitTime = storedLastVisit
        ? parseInt(storedLastVisit, 10)
        : currentTime;

      const timeDifference = currentTime - lastVisitTime;
      const shouldRefetch = timeDifference > REFETCH_THRESHOLD_MS;
      localStorage.setItem(REFETCH_ON_FOCUS_KEY, shouldRefetch.toString());

      // If more than 5 minutes have passed, refetch all APIs
      if (shouldRefetch && !hasRefetched.current) {
        hasRefetched.current = true;
        // const minutesElapsed = Math.round(timeDifference / 60000); // Use this if you want to log the elapsed time in minutes for debugging
        console.info(
          "[Visit Tracking] Window regained focus - Refetching APIs...",
        );
        refetchAllApis();
      }

      localStorage.setItem(LAST_VISIT_KEY, currentTime.toString());
    } else if (documentVisible === false) {
      // Window is being hidden - store current timestamp and reset refetch flag
      localStorage.setItem(LAST_VISIT_KEY, Date.now().toString());
      hasRefetched.current = false;
    }
  }, [documentVisible]);

  // Getter function to retrieve the time difference
  const getTimeSinceLastVisit = (): number | null => {
    const lastVisitStr = localStorage.getItem(LAST_VISIT_KEY);
    if (!lastVisitStr) return null;

    const lastVisit = parseInt(lastVisitStr, 10);
    return Date.now() - lastVisit;
  };

  // Getter function to retrieve the last visit timestamp
  const getLastVisitTimestamp = (): number | null => {
    const lastVisitStr = localStorage.getItem(LAST_VISIT_KEY);
    return lastVisitStr ? parseInt(lastVisitStr, 10) : null;
  };

  // Helper function to format milliseconds to readable string using dayjs
  const formatTimeDifference = (ms: number): string => {
    const now = dayjs();
    const lastVisit = dayjs(now).subtract(ms, "milliseconds");
    return lastVisit.fromNow(); // e.g., "2 hours ago", "5 minutes ago"
  };

  return {
    getTimeSinceLastVisit,
    getLastVisitTimestamp,
    formatTimeDifference,
  };
};
